import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { FileStore, Mutation, Query, FsFile, FsFileUploadRequest, FileStoreOperationLog, FsFileUpdateInput } from 'core-lib';
import {from, Observable, of} from 'rxjs';
import {catchError, map, mergeMap, toArray} from 'rxjs/operators';
import {HttpClient, HttpEventType, HttpRequest} from "@angular/common/http";
import {ToastMessageOptions} from "primeng/api";

@Injectable({
  providedIn: 'root'
})
export class FilestoreService {

  FILE_PROPERTIES = `id
       contentType
       directory
       parentId
       contentLength
       status
       thumbnailImageUrl
       downloadUrl
       optimizedUrl
       name
       displayName
       created
       createdBy
       lastModified
       lastModifiedBy
       altText`;

  QUERY_FILESTORE = gql`
  query fileStore($owner: ID!, $loadFiles: Boolean!){
    fileStore(owner: $owner, loadFiles: $loadFiles){
      id
      writeAccess
      operationLogAccess
      acceptedFileTypes
      files {
        ${this.FILE_PROPERTIES}
      }
    },
  }`;

  QUERY_FILE_EXISTS = gql`
  query fileStoreFileExists($storeId: FSFileId!, $name: String!,$parent: FSFileId){
    fileStoreFileExists(storeId: $storeId, name: $name,parent: $parent)
  }`;

  LOAD_FILESTORE_FILE = gql`
  query loadFileStoreFile($storeId: FSFileId!, $fileId: FSFileId!){
    loadFileStoreFile(storeId: $storeId, fileId: $fileId){
        ${this.FILE_PROPERTIES}
    }
  }`;

  QUERY_OPERATION_LOGS = gql`
  query fileStoreOperationLogs($storeId: FSFileId!){
    fileStoreOperationLogs(storeId: $storeId){
      id
      created
      executor
      source
      destination
      operationType
      downloadUrl
    }
  }`;


  MUTATE_CREATE_DIRECTORY = gql`
  mutation createFileStoreDirectory($storeId: FSId, $name: String!,$parent: FSFileId){
    createFileStoreDirectory(storeId: $storeId, name: $name, parent: $parent){
        ${this.FILE_PROPERTIES}
    },
  }`;


  MUTATE_CREATE_FILE = gql`
  mutation createFileStoreFile($storeId: FSId, $request: FSFileUploadRequest!,$parent: FSFileId){
    createFileStoreFile(storeId: $storeId, request: $request, parent: $parent){
        ${this.FILE_PROPERTIES}
        uploadUrl
    },
  }`;

  MUTATE_RENAME_FILE = gql`
  mutation renameFileStoreFile($storeId: FSId, $file: FSFileId!,$newName: String!){
    renameFileStoreFile(storeId: $storeId, file: $file, newName: $newName){
        ${this.FILE_PROPERTIES}
    },
  }`;

  MUTATION_UPDATE_FILESTORE_FILE = gql`
  mutation updateFileStoreFile($storeId: FSFileId!, $file: FSFileId!, $update: FSFileUpdateInput!){
    updateFileStoreFile(storeId: $storeId, file: $file, update: $update){
        ${this.FILE_PROPERTIES}
    },
  }`;

  MUTATE_CREATE_FILE_COMPLETE = gql`
  mutation fileStoreUploadComplete($storeId: FSId, $file: FSFileId!){
    fileStoreUploadComplete(storeId: $storeId, file: $file){
        ${this.FILE_PROPERTIES}
    }
  }`;

  MUTATE_MOVE_FILES = gql`
  mutation fileStoreMoveFiles($storeId: FSId, $files: [FSFileId]!,$newParent: FSFileId){
    fileStoreMoveFiles(storeId: $storeId, files: $files,newParent: $newParent){
        ${this.FILE_PROPERTIES}
    }
  }`;

  MUTATE_DELETE_FILES = gql`
  mutation deleteFileStoreFiles($storeId: FSId, $files: [FSFileId]!){
    deleteFileStoreFiles(storeId: $storeId, files: $files)
  }`;

  constructor(private apollo: Apollo, private http: HttpClient) {

  }

  /**
   * loads the filestore from backend
   * @param ownerId
   * @param loadFiles
   * @returns
   */
  loadFileStore(ownerId: string, loadFiles: boolean): Observable<FileStore> {
    return this.apollo.query<Query>({
      query: this.QUERY_FILESTORE,
      variables: {
        owner: ownerId,
        loadFiles: loadFiles
      },
    }).pipe(map(result => result.data.fileStore));
  }


  /**
   * load the operations logs
   * @param storeId
   * @returns
   */
  loadOperationLogs(storeId: string) : Observable<FileStoreOperationLog[]> {
    return this.apollo.query<Query>({
      query: this.QUERY_OPERATION_LOGS,
      variables: {
        storeId: storeId
      },
    }).pipe(map(result => result.data.fileStoreOperationLogs));
  }


  /**
   * Creates file ready for upload
   * @param storeId
   * @param file
   * @param parent
   * @returns
   */
  createFile(storeId: string, request: FsFileUploadRequest, parent?: string): Observable<{ file: FsFile, error: string }> {
    const variables = {
      storeId: storeId,
      request: request,
    };
    if (parent) {
      variables['parent'] = parent;
    }
    return this.apollo.mutate<Mutation>({
      mutation: this.MUTATE_CREATE_FILE,
      variables,
    }).pipe(map(result => { return { file: result.data.createFileStoreFile, error: null } }), catchError(error => {
      return of({ file: null, error: error.message });
    }));
  }

  /**
   * Upload completed. Trigger event complete
   * @param storeId
   * @param file
   * @returns
   */
  fileStoreUploadComplete(storeId: string, file: string): Observable<{ file: FsFile, error: string }> {
    return this.apollo.mutate<Mutation>({
      mutation: this.MUTATE_CREATE_FILE_COMPLETE,
      variables: {
        storeId: storeId,
        file: file,
      }
    }).pipe(map(result => { return { file: result.data.fileStoreUploadComplete, error: null } }), catchError(error => {
      return of({ file: null, error: error.message });
    }));
  }

  /**
   * create a directory
   * @param storeId
   * @param name
   * @param parent
   * @returns
   */
  createDirectory(storeId: string, name: string, parent?: string): Observable<FsFile> {
    const variables = {
      storeId: storeId,
      name: name
    };
    if (parent) {
      variables['parent'] = parent;
    }
    return this.apollo.mutate<Mutation>({
      mutation: this.MUTATE_CREATE_DIRECTORY,
      variables
    }).pipe(map(result => result.data.createFileStoreDirectory));
  }


  /**
   * check if file exists
   * @param storeId
   * @param name
   * @param parentId
   * @returns
   */
  fileStoreFileExists(storeId: string, name: string, parentId?: string): Observable<boolean> {
    const variables = {
      storeId: storeId,
      name: name
    };
    if (parent) {
      variables['parent'] = parentId;
    }

    return this.apollo.query<Query>({
      query: this.QUERY_FILE_EXISTS,
      variables
    }).pipe(map(result => result.data.fileStoreFileExists));
  }


  /**
   * Löscht die dateien
   * @param storeId
   * @param files
   * @returns
   */
  deleteFiles(storeId: string, files: string[]): Observable<number> {
    const variables = {
      storeId: storeId,
      files: files
    };

    return this.apollo.mutate<Mutation>({
      mutation: this.MUTATE_DELETE_FILES,
      variables
    }).pipe(map(result => result.data.deleteFileStoreFiles));
  }


  /**
   * benennt eine Datei oder Verzeichnis um
   * @param storeId
   * @param file
   * @param newName
   * @returns
   */
  renameFileStoreFile(storeId: string, file: string, newName: string): Observable<FsFile> {
    const variables = {
      storeId: storeId,
      file: file,
      newName: newName
    };

    return this.apollo.mutate<Mutation>({
      mutation: this.MUTATE_RENAME_FILE,
      variables
    }).pipe(map(result => result.data.renameFileStoreFile));
  }

 /**
  * Aktualisiert die Metadaten einer Datei
  * @param storeId
  * @param file
  * @param newName
  * @returns
  */
  updateFileStoreFile(storeId: string, file: string, update: FsFileUpdateInput): Observable<FsFile> {
    const variables = {
      storeId: storeId,
      file: file,
      update
    };

    return this.apollo.mutate<Mutation>({
      mutation: this.MUTATION_UPDATE_FILESTORE_FILE,
      variables
    }).pipe(map(result => result.data.updateFileStoreFile));
  }

  /**
   * verschiebt die datein
   * @param storeId
   * @param files
   * @param newParent
   * @returns
   */
  moveFiles(storeId: string, files: string[], newParent: string): Observable<FsFile[]> {
    const variables = {
      storeId: storeId,
      files: files
    };
    if (newParent) {
      variables['newParent'] = newParent;
    }
    return this.apollo.mutate<Mutation>({
      mutation: this.MUTATE_MOVE_FILES,
      variables
    }).pipe(map(result => result.data.fileStoreMoveFiles));
  }

  /**
   * lädt eine einzelne datei
   * @param storeId
   * @param fileId
   * @returns
   */
  loadFileStoreFile(storeId: string, fileId: string): Observable<FsFile> {
    const variables = {
      storeId: storeId,
        fileId: fileId
    };
    return this.apollo.query<Query>({
      query: this.LOAD_FILESTORE_FILE,
      variables
    }).pipe(map(result => result.data.loadFileStoreFile));
  }

  /**
   * lädt eine einzelne datei
   * @param files
   * @param filestoreId
   * @returns
   */
  createFilesFromUpload(files: File[], filestoreId): Observable<{ file: File, fsFile: FsFile, error:string }[]> {

     const fsFileUploadRequests = files.map((file) => {
         const request = {} as FsFileUploadRequest;
         request.contentLength = file.size;
         request.name = file.name;
         request.contentType = file.type;
         return { file, request };
     });

     // this.uploadStatus.running = true;

     return from(fsFileUploadRequests).pipe(
         mergeMap(({ file, request }) =>
                 this.createFile(filestoreId, request, null).pipe(
                     map(mutationResult => ({ file, fsFile: mutationResult.file, error: mutationResult.error }))
                 ),
             2 // Maximal 2 gleichzeitige Datei-Erstellungen
         ),
         mergeMap(({ file, fsFile, error }) => {
             if (error) {
                 // TODO: kann man den uploadstatus übergeben/emitten, ohne zu returnen?
                 // this.uploadStatus.details[file.name] = { progress: 100, complete: true, message: error, error: true };
                 return of({ file, fsFile, error });
             }

             const req = new HttpRequest('PUT', fsFile.uploadUrl, file, { reportProgress: true });
             // this.uploadStatus.details[file.name] = { progress: 0, complete: false, message: null, error: null };

             return new Observable<{ file: File, fsFile: FsFile, error?: string }>((observer) => {
                 this.http.request(req).subscribe({
                     next: (event) => {
                         if (event.type === HttpEventType.UploadProgress) {
                             const percentDone = Math.round(100 * event.loaded / (event.total ?? 0));
                             // this.uploadStatus.details[file.name].progress = percentDone;
                         } else if (event.type === HttpEventType.Response) {
                             observer.next({ file, fsFile });
                             observer.complete();
                         }
                     },
                     error: (err) => {
                         observer.next({ file, fsFile, error: err.message });
                         observer.complete();
                     }
                 });
             });
         }, 2), // Maximal 2 gleichzeitige Uploads
         mergeMap(({ file, fsFile, error }) => {
             if (error) {
                 return of({ file, fsFile, error });
             } else {
                 return this.fileStoreUploadComplete(filestoreId, fsFile.id).pipe(
                     map(result => ({ file, fsFile: result.file, error: result.error }))
                 );
             }
         }, 2), // Maximal 2 gleichzeitige "complete"-Calls
         toArray()
     )
  }
}
